import GlassCard from '@/components/GlassCard';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface Answer {
    id: number;
    answer: string;
    is_correct: boolean;
}

export interface QuestionDetail {
    id: number;
    question: string;
    question_type: 'single' | 'cloze' | 'sort' | 'matrix';
    is_correct: boolean | null;
    user_selected_answer_ids: number[];
    cloze_responses: string[];
    response_data: unknown[];
    answers: Answer[];
    cloze_segments: unknown[];
    sort_items: unknown[];
    matrix_pairs: unknown[];
}

const CorrectIcon: React.FC = () => (
    <svg
        className="h-5 w-5 shrink-0 text-green-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
);

const WrongIcon: React.FC = () => (
    <svg
        className="h-5 w-5 shrink-0 text-red-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
        />
    </svg>
);

interface SingleAnswersProps {
    answers: Answer[];
    userSelectedIds: number[];
}

const SingleAnswers: React.FC<SingleAnswersProps> = ({
    answers,
    userSelectedIds,
}) => {
    const { t } = useTranslation();

    return (
        <ul className="mt-3 space-y-2">
            {answers.map((answer) => {
                const isSelected = userSelectedIds.includes(answer.id);
                const isCorrect = answer.is_correct;

                let rowClass =
                    'flex items-start gap-2 rounded-lg px-3 py-2 text-sm';
                if (isSelected && isCorrect) {
                    rowClass +=
                        ' bg-green-100/70 text-green-900 dark:bg-green-900/20 dark:text-green-300';
                } else if (isSelected && !isCorrect) {
                    rowClass +=
                        ' bg-red-100/70 text-red-900 dark:bg-red-900/20 dark:text-red-300';
                } else if (!isSelected && isCorrect) {
                    rowClass +=
                        ' bg-green-50/50 text-green-800 dark:bg-green-900/10 dark:text-green-400';
                } else {
                    rowClass += ' text-gray-600 dark:text-neutral-400';
                }

                return (
                    <li key={answer.id} className={rowClass}>
                        <span className="mt-0.5 shrink-0">
                            {isSelected && isCorrect ? <CorrectIcon /> : null}
                            {isSelected && !isCorrect ? <WrongIcon /> : null}
                            {!isSelected && isCorrect ? (
                                <svg
                                    className="h-5 w-5 text-green-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M9 12l2 2 4-4"
                                    />
                                </svg>
                            ) : null}
                            {!isSelected && !isCorrect ? (
                                <span className="inline-block h-5 w-5" />
                            ) : null}
                        </span>
                        <span
                            className={clsx(
                                'flex-1',
                                isSelected && 'font-medium'
                            )}
                            dangerouslySetInnerHTML={{ __html: answer.answer }}
                        />
                        {isSelected && !isCorrect && (
                            <span className="shrink-0 text-xs text-red-500 dark:text-red-400">
                                {t('evaluation.review.yourAnswerBadge')}
                            </span>
                        )}
                        {!isSelected && isCorrect && (
                            <span className="shrink-0 text-xs text-green-600 dark:text-green-400">
                                {t('evaluation.review.correctAnswerBadge')}
                            </span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

interface ClozeAnswersProps {
    clozeResponses: string[];
}

const ClozeAnswers: React.FC<ClozeAnswersProps> = ({ clozeResponses }) => {
    const { t } = useTranslation();

    if (clozeResponses.length === 0) {
        return (
            <p className="mt-2 text-sm text-gray-400 italic">
                {t('evaluation.review.noAnswer')}
            </p>
        );
    }
    return (
        <div className="mt-3 space-y-1">
            {clozeResponses.map((response, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500 dark:text-neutral-500">
                        {t('evaluation.review.blankLabel', { index: i + 1 })}:
                    </span>
                    <span className="font-medium text-gray-800 dark:text-neutral-200">
                        {response || '—'}
                    </span>
                </div>
            ))}
        </div>
    );
};

interface QuestionCardProps {
    question: QuestionDetail;
    index: number;
}

const QuestionCard: React.FC<QuestionCardProps> = ({ question, index }) => {
    const { t } = useTranslation();
    const { is_correct } = question;
    const unanswered =
        question.user_selected_answer_ids.length === 0 &&
        question.cloze_responses.length === 0 &&
        question.response_data.length === 0;

    let borderColor = 'border-transparent';
    if (is_correct === true) {
        borderColor = 'border-green-400/60 dark:border-green-500/40';
    } else if (is_correct === false) {
        borderColor = 'border-red-400/60 dark:border-red-500/40';
    }

    return (
        <GlassCard className={clsx('border p-5', borderColor)}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0 text-sm font-semibold text-gray-400 dark:text-neutral-500">
                        {index + 1}.
                    </span>
                    <div
                        className="text-sm font-medium text-gray-900 dark:text-white [&_audio]:mt-1 [&_p]:mb-1"
                        dangerouslySetInnerHTML={{ __html: question.question }}
                    />
                </div>
                <div className="shrink-0">
                    {is_correct === true && <CorrectIcon />}
                    {is_correct === false && <WrongIcon />}
                    {is_correct === null && (
                        <span className="inline-block h-5 w-5 rounded-full bg-gray-300 dark:bg-neutral-600" />
                    )}
                </div>
            </div>

            {unanswered && (
                <p className="mt-3 text-sm text-gray-400 italic dark:text-neutral-500">
                    {t('evaluation.review.notAnswered')}
                </p>
            )}

            {!unanswered && question.question_type === 'single' && (
                <SingleAnswers
                    answers={question.answers}
                    userSelectedIds={question.user_selected_answer_ids}
                />
            )}

            {!unanswered && question.question_type === 'cloze' && (
                <ClozeAnswers clozeResponses={question.cloze_responses} />
            )}

            {!unanswered &&
                (question.question_type === 'sort' ||
                    question.question_type === 'matrix') && (
                    <p className="mt-3 text-sm text-gray-500 dark:text-neutral-400">
                        {is_correct === true
                            ? t('evaluation.review.orderCorrect')
                            : t('evaluation.review.orderIncorrect')}
                    </p>
                )}
        </GlassCard>
    );
};

interface EvaluationAnswersReviewProps {
    questions: QuestionDetail[];
}

const EvaluationAnswersReview: React.FC<EvaluationAnswersReviewProps> = ({
    questions,
}) => {
    const { t } = useTranslation();

    if (questions.length === 0) {
        return (
            <GlassCard className="p-6 text-center">
                <p className="text-sm text-gray-500 dark:text-neutral-400">
                    {t('evaluation.results.answersUnavailable')}
                </p>
            </GlassCard>
        );
    }

    return (
        <div className="space-y-4">
            {questions.map((question, index) => (
                <QuestionCard
                    key={question.id}
                    question={question}
                    index={index}
                />
            ))}
        </div>
    );
};

export default EvaluationAnswersReview;
